import {
  IFeeCategory,
  IFeeComponentDefinition,
  IFeeStructure,
  IAdminGetFeeCategoryResponse,
  IAdminGetGradeFeeStructureResponse,
  IAdminGetPricingRuleResponse,
  IClassAndFeeStructureSchemaResponse,
  IClassCategory,
  IClassStructureSchema,
} from './institute.interfaces';

const ADMIN_FEE_BASE = '/admin/feeStructure';

export { ADMIN_FEE_BASE };

export function resolveAdminFeeStructureInstituteId(explicit?: string): string {
  if (explicit?.trim()) return explicit.trim();
  const token =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('api-token') ?? window.localStorage.getItem('token') ?? ''
      : '';
  const encodedPayload = token.split('.')[1];
  if (!encodedPayload) return '';
  try {
    const payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4),
      '='
    );
    const decoded = JSON.parse(window.atob(paddedPayload)) as Record<
      string,
      unknown
    >;
    const ref = decoded.institute_ref ?? decoded.instituteId;
    return typeof ref === 'string' ? ref : '';
  } catch {
    return '';
  }
}

export function normalizeArrayResponse<T>(body: unknown): T[] {
  return Array.isArray(body) ? body : [];
}

function ruleAmount(
  rule: IAdminGetPricingRuleResponse,
  categoryFallback: number
): number {
  if (
    typeof rule.fixedAmount === 'number' &&
    !Number.isNaN(rule.fixedAmount)
  ) {
    return rule.fixedAmount;
  }
  if (
    typeof rule.perGradeAmount === 'number' &&
    !Number.isNaN(rule.perGradeAmount)
  ) {
    return rule.perGradeAmount;
  }
  if (
    typeof rule.perClassAmount === 'number' &&
    !Number.isNaN(rule.perClassAmount)
  ) {
    return rule.perClassAmount;
  }
  return categoryFallback;
}

export function mapAdminCategoryToGradeCategory(
  cat: IAdminGetFeeCategoryResponse,
  pricingRules: IAdminGetPricingRuleResponse[]
): IClassCategory {
  const activeRules = pricingRules.filter((r) => r.isActive);
  const feeComponents: IFeeStructure[] =
    activeRules.length > 0
      ? activeRules.map((rule) => ({
        feeId: rule.id,
        feeAmount: ruleAmount(rule, cat.amount),
        feeType: rule.ruleType,
        feeDescription:
          [rule.paymentTerm, rule.customFormula].filter(Boolean).join(' · ') ||
          cat.description ||
          '',
        isOptional: false,
      }))
      : [
        {
          feeId: cat.id,
          feeAmount: cat.amount,
          feeType: cat.feeType || cat.name,
          feeDescription: cat.description ?? '',
          isOptional: false,
        },
      ];

  return {
    categoryId: cat.id,
    category: cat.name,
    categoryDescription: cat.description ?? '',
    feeComponents,
    requiredDocuments: [],
  };
}

export function mapAdminCategoryToFeeCategory(
  cat: IAdminGetFeeCategoryResponse,
  pricingRules: IAdminGetPricingRuleResponse[]
): IFeeCategory {
  const activeRules = pricingRules.filter((r) => r.isActive);
  const components: IFeeComponentDefinition[] =
    activeRules.length > 0
      ? activeRules.map((rule) => ({
        componentId: rule.id,
        componentName: rule.ruleType,
        amount: ruleAmount(rule, cat.amount),
        isOptional: false,
        description:
          [rule.paymentTerm, rule.customFormula].filter(Boolean).join(' · ') ||
          cat.description,
        currencyCode: cat.currency,
      }))
      : [
        {
          componentId: cat.id,
          componentName: cat.name,
          amount: cat.amount,
          isOptional: false,
          description: cat.description,
          currencyCode: cat.currency,
        },
      ];

  return {
    categoryId: cat.id,
    categoryName: cat.name,
    categoryDescription: cat.description,
    components,
  };
}

/** Maps one class-fees category (student category + computed fee rows) to admission Step 4 shape */
export function mapClassCategoryToAdmissionFeeCategory(
  cat: IClassCategory
): IFeeCategory {
  const components: IFeeComponentDefinition[] = cat.feeComponents.map(
    (fc) => ({
      componentId: fc.feeId,
      componentName: fc.feeDescription?.trim() ? fc.feeDescription : fc.feeType,
      amount: fc.feeAmount,
      isOptional: fc.isOptional ?? false,
      description: fc.feeDescription,
    })
  );

  return {
    categoryId: cat.categoryId,
    categoryName: cat.category,
    categoryDescription: cat.categoryDescription,
    components,
  };
}

export function mapGradeStructuresToSchemaResponse(
  gradeRows: IAdminGetGradeFeeStructureResponse[],
  categoryById: Map<string, IAdminGetFeeCategoryResponse>,
  pricingRulesByCategoryId: Map<string, IAdminGetPricingRuleResponse[]>
): IClassAndFeeStructureSchemaResponse {
  const grades: IClassStructureSchema[] = gradeRows.map((row) => {
    const categoryIds = normalizeArrayResponse<string>(row.categories);
    const categories: IClassCategory[] = categoryIds
      .map((id) => categoryById.get(id))
      .filter((c): c is IAdminGetFeeCategoryResponse => Boolean(c))
      .map((cat) =>
        mapAdminCategoryToGradeCategory(
          cat,
          pricingRulesByCategoryId.get(cat.id) ?? []
        )
      );

    return {
      classId: row.gradeId,
      className: row.gradeName ?? row.gradeId,
      classDescription: row.gradeDescription ?? '',
      sections: [],
      categories,
    };
  });

  return { classes: grades };
}

export function findGradeStructureRow(
  rows: IAdminGetGradeFeeStructureResponse[],
  gradeKey: string
): IAdminGetGradeFeeStructureResponse | undefined {
  const trimmed = gradeKey.trim();
  return rows.find((g) => {
    const name = g.gradeName;
    return (
      g.gradeId === trimmed ||
      (name && name === trimmed) ||
      (name && name.toLowerCase() === trimmed.toLowerCase()) ||
      g.gradeId.toLowerCase() === trimmed.toLowerCase()
    );
  });
}
